import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { bt } from '../../i18n';
import { clamp, hexToHsv, hsvToHex } from './colorUtils';

/**
 * HSV color picker (Shopify/Chrome look): a saturation/value square + a hue slider + a hex field + an
 * eyedropper, EMBEDDED (so it can live inside the color popover AND the gradient popover — the native
 * `<input type=color>` can't be embedded). Emits `#rrggbb` on every change. The eyedropper uses the
 * native `EyeDropper` API when present (Chromium); hidden otherwise.
 */
type Hsv = { h: number; s: number; v: number };

/** Track a pointer drag across the window so the handle keeps following outside the element. */
function startDrag(handler: (e: PointerEvent) => void, e: React.PointerEvent) {
  handler(e.nativeEvent);
  const move = (ev: PointerEvent) => handler(ev);
  const up = () => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
  };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
}

export function HsvColorPicker({
  value,
  onChange,
  hexLabel,
}: {
  value: string;
  onChange: (hex: string) => void;
  hexLabel: string;
}) {
  const [hsv, setHsv] = useState<Hsv>(() => hexToHsv(value) ?? { h: 270, s: 60, v: 55 });
  const [hexText, setHexText] = useState(value);
  const squareRef = useRef<HTMLDivElement>(null);
  const hueRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setHexText(value);
    const c = hexToHsv(value);
    if (c) setHsv(c);
  }, [value]);

  const commit = (next: Hsv) => {
    setHsv(next);
    const hex = hsvToHex(next.h, next.s, next.v);
    setHexText(hex);
    onChange(hex);
  };

  const onSquare = (e: PointerEvent) => {
    const el = squareRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = clamp((e.clientX - r.left) / r.width, 0, 1);
    const y = clamp((e.clientY - r.top) / r.height, 0, 1);
    commit({ h: hsv.h, s: Math.round(x * 100), v: Math.round((1 - y) * 100) });
  };
  const onHue = (e: PointerEvent) => {
    const el = hueRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = clamp((e.clientX - r.left) / r.width, 0, 1);
    commit({ ...hsv, h: Math.round(x * 360) });
  };

  const eyeSupported = typeof window !== 'undefined' && 'EyeDropper' in window;
  const openEye = async () => {
    try {
      const Eye = (
        window as unknown as { EyeDropper: new () => { open: () => Promise<{ sRGBHex: string }> } }
      ).EyeDropper;
      const res = await new Eye().open();
      const c = hexToHsv(res.sRGBHex);
      if (c) commit(c);
    } catch {
      /* cancelled */
    }
  };

  return (
    <div className="bld-hsv">
      <div
        ref={squareRef}
        className="bld-hsv__square"
        style={{ '--bld-hue': hsv.h } as CSSProperties}
        onPointerDown={(e) => startDrag(onSquare, e)}
      >
        <div className="bld-hsv__square-sat" />
        <div className="bld-hsv__square-val" />
        <div
          className="bld-hsv__square-handle"
          style={{ left: `${hsv.s}%`, top: `${100 - hsv.v}%` }}
        />
      </div>

      <div className="bld-hsv__row">
        <span className="bld-hsv__preview" aria-hidden="true" style={{ background: hexText }} />
        <div className="bld-hsv__hexwrap">
          <span className="bld-hsv__hash" aria-hidden="true">
            #
          </span>
          <input
            className="bld-hsv__hex"
            type="text"
            aria-label={hexLabel}
            value={hexText.replace(/^#/, '')}
            onChange={(e) => {
              const v = `#${e.target.value.replace(/^#/, '')}`;
              setHexText(v);
              const c = hexToHsv(v);
              if (c) {
                setHsv(c);
                onChange(v);
              }
            }}
          />
        </div>
        {eyeSupported && (
          <button
            type="button"
            className="bld-hsv__eye"
            aria-label={bt('chrome.color.eyedropper', 'Pick a color from the screen')}
            onClick={openEye}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="m2 22 1-1h3l9-9"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M3 21v-3l9-9"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="m15 6 3.4-3.4a2.1 2.1 0 1 1 3 3L18 9l.4.4a2.1 2.1 0 1 1-3 3l-3.8-3.8a2.1 2.1 0 1 1 3-3l.4.4Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>

      <div
        ref={hueRef}
        className="bld-hsv__hue"
        onPointerDown={(e) => startDrag(onHue, e)}
        role="slider"
        aria-label={bt('chrome.color.hue', 'Hue')}
        aria-valuemin={0}
        aria-valuemax={360}
        aria-valuenow={hsv.h}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') commit({ ...hsv, h: clamp(hsv.h - 5, 0, 360) });
          if (e.key === 'ArrowRight') commit({ ...hsv, h: clamp(hsv.h + 5, 0, 360) });
        }}
      >
        <div className="bld-hsv__hue-handle" style={{ left: `${(hsv.h / 360) * 100}%` }} />
      </div>
    </div>
  );
}
